import type { GiftCardData } from '@cencosud-cencommerce/paris-uikit/dist/utils/types/PurchaseSummary.type';

import { useAppSelector } from '@/Application/Hooks/Redux';
import type { Cart } from '@/modules/cart/domain/Cart';

import type { ISinglePayment } from '../interfaces/src/bff-web/billing/interfaces/payment-methods.interfaceV2';
import { PaymentProcessor } from '../interfaces/src/bff-web/billing/interfaces/payment-methods.interfaceV2';

import { formatPrice } from './utils';

export const useGiftCardData = () => {
  const { data: cart } = useAppSelector((state) => state.cart);
  const { data: cartPayments } = useAppSelector((state) => state.cartPayments);

  const getGiftCard = () => {
    const giftCard = cart?.payments?.find(
      (payment) => payment.paymentMethodInfo.method === 'giftCard'
    );
    return giftCard;
  };

  const getInterfaceInteractions = () => {
    const giftCard = getGiftCard();
    return giftCard?.interfaceInteractions?.find((interaction) => interaction);
  };

  const getGiftCartToken = () => {
    if (!cart) return '';
    const interfaceInteractions = getInterfaceInteractions();
    return interfaceInteractions?.token;
  };

  const getGiftCardAmount = () => {
    if (!cart) return 0;
    let amount: number = 0;
    const giftCardDetails = getGiftCardDetails();
    giftCardDetails?.details?.map((detail) => {
      amount += detail.amount;
    });
    return amount;
  };

  const getGiftCardBalance = () => {
    if (!cart) return 0;
    const giftCardDetails = getGiftCardDetails();
    const balance = giftCardDetails?.balance ?? 0;
    return balance;
  };

  const getGiftCardDetails = () => {
    if (!cart) return;
    const interfaceInteractions = getInterfaceInteractions();

    return interfaceInteractions?.giftCardDetails;
  };

  const cartHasGiftCardPayment = () => {
    if (!cart) return false;
    const giftCard = getGiftCard();
    return !!giftCard;
  };

  const canGiftCardPayTotalAmount = () => {
    if (!cart) return false;

    const amount = getGiftCardAmount();
    if (amount === 0) return false;

    const totalAmount = cartPayments?.otherMethod?.centAmount ?? 0;
    if (amount < totalAmount) return false;

    return true;
  };

  const getGiftCardPayment = () => {
    const giftCardPayment: ISinglePayment = {
      paymentProcessor: PaymentProcessor.GIFT_CARD,
      paymentToken: getGiftCartToken()
    };
    return giftCardPayment;
  };

  const getFormattedGiftCardData = (): GiftCardData => {
    const giftCardDetails = getGiftCardDetails();

    let discount: number = 0;
    giftCardDetails?.details?.map((detail) => {
      discount += detail.amount;
    });
    const balance = giftCardDetails?.balance ?? 0;

    const appliedAmount = formatPrice(discount) ?? '';
    const balanceAmount = formatPrice(balance) ?? '';

    return {
      appliedAmount,
      balanceAmount
    };
  };

  const getProductsNotCompatibleWithGiftCard = () => {
    if (!cart) return [];
    const giftCardDetails = getGiftCardDetails();
    const innerDetails = giftCardDetails?.details.find((detail) => detail);
    if (!innerDetails) return [];

    const isIncompatible = innerDetails?.amount === 0;
    const departmentId = innerDetails?.depto;
    const last3DepartmentId = departmentId?.slice(-3);

    const productsNotCompatible = isIncompatible
      ? cart?.lineItems?.filter((lineItem) => {
          const departmentIdValue = lineItem?.variant?.attributes?.find(
            (attr) => attr?.name === 'departmentId'
          )?.value;

          if (!departmentIdValue) return false;

          return departmentIdValue === last3DepartmentId;
        })
      : [];

    return productsNotCompatible;
  };

  return {
    canGiftCardPayTotalAmount,
    cartHasGiftCardPayment,
    getGiftCardAmount,
    getGiftCard,
    getGiftCardPayment,
    getGiftCardBalance,
    getGiftCartToken,
    getFormattedGiftCardData,
    getProductsNotCompatibleWithGiftCard
  };
};

export const getProductsNotCompatibleWithGiftCard = (cart: Cart) => {
  if (!cart) return [];
  const giftCard = cart?.payments?.find(
    (payment) => payment?.paymentMethodInfo?.method === 'giftCard'
  );
  const interfaceInteraction = giftCard?.interfaceInteractions?.find(
    (interaction) => interaction
  );
  const giftCardDetails = interfaceInteraction?.giftCardDetails;
  const innerDetails = giftCardDetails?.details?.find((detail) => detail);
  if (!innerDetails) return [];

  const isIncompatible = innerDetails?.amount === 0;
  const departmentId = innerDetails?.depto;
  const last3DepartmentId = departmentId?.slice(-3);

  const productsNotCompatible = isIncompatible
    ? cart?.lineItems?.filter((lineItem) => {
        const departmentIdValue = lineItem?.variant?.attributes?.find(
          (attr) => attr?.name === 'departmentId'
        )?.value;

        if (!departmentIdValue) return false;

        return departmentIdValue === last3DepartmentId;
      })
    : [];

  return productsNotCompatible;
};
